import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, BookOpen, ArrowRight } from "lucide-react";
import { articles } from "@/data/articles";

const SITE_ORIGIN = "https://cosomicsage.com";

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function Learn() {
  useEffect(() => {
    const url = `${SITE_ORIGIN}/learn`;
    const desc = "In-depth guides on Vedic astrology, numerology, Vastu and palmistry. Learn Vimshottari Dasha, Lo Shu grid, Guna Milan, Panchang and more — free and original.";
    document.title = "Learn — Vedic Astrology & Numerology Guides | Cosomic Sage";
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:title"]', "content", "Vedic Astrology & Numerology Guides");
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:type"]', "content", "website");
  }, []);

  const categories = [...new Set(articles.map((a) => a.category))];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 starfield" data-testid="learn-page">
      <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-serif text-lg text-slate-50">Cosmic Sage</span>
          </Link>
          <Link to="/dashboard" className="text-sm text-amber-300 hover:text-amber-200" data-testid="learn-dashboard-link">
            Open the App →
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/5 mb-6">
            <BookOpen className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs tracking-wider uppercase text-amber-300">Guides & Articles</span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-slate-50 leading-tight mb-5">
            Learn Vedic astrology & numerology — <span className="text-amber-300 italic">the honest way</span>
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            In-depth, non-promotional guides written for people who want to actually understand the
            tradition — not get sold a reading. Every article is original, grounded in classical
            Jyotish and numerology texts, and free to read.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="learn-categories">
          {categories.map((c) => (
            <span key={c} className="px-3 py-1 rounded-full text-xs text-slate-300 border border-white/10 bg-white/5">
              {c}
            </span>
          ))}
        </div>

        <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="learn-articles-grid">
          {articles.map((a, i) => (
            <Link
              key={a.slug}
              to={`/learn/${a.slug}`}
              data-testid={`learn-article-card-${a.slug}`}
              className="group block p-6 rounded-2xl bg-slate-900/50 border border-white/5 hover:border-amber-500/30 transition-colors"
            >
              <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                <span className="text-amber-300/80 uppercase tracking-wider">{a.category}</span>
                <span className="text-slate-700">·</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {a.readMinutes} min read
                </span>
              </div>
              <h2 className="font-serif text-2xl text-slate-50 group-hover:text-amber-200 transition-colors leading-snug mb-3">
                {a.title}
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">{a.description}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-amber-300">
                Read the guide <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </section>

        <section className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-amber-500/10 to-slate-900 border border-amber-500/20 text-center">
          <h2 className="font-serif text-3xl text-slate-50 mb-3">Ready to put it into practice?</h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-6">
            Compute your free Vedic birth chart, numerology profile and Panchang — no account required.
          </p>
          <Link
            to="/dashboard"
            data-testid="learn-cta-dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
          >
            Open the free app <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <footer className="mt-12 pt-6 border-t border-white/5 text-sm text-slate-500 flex flex-wrap gap-4">
          <Link to="/" className="hover:text-amber-300">Home</Link>
          <Link to="/about" className="hover:text-amber-300">About</Link>
          <Link to="/privacy" className="hover:text-amber-300">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-amber-300">Terms of Service</Link>
          <a href="mailto:support@cosomicsage.com" className="hover:text-amber-300">Contact</a>
        </footer>
      </main>
    </div>
  );
}
